import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../types";
import {
  buildIneffectiveBreakdownFromAggregates,
  computeIneffectiveBreakdown,
  type IneffectiveBreakdown,
} from "../utils";

interface UseCockpitIneffectiveBreakdownParams {
  match: Match | null;
  liveEvents: MatchEvent[];
  queuedEvents: MatchEvent[];
  ineffectiveTick: number;
}

export const useCockpitIneffectiveBreakdown = ({
  match,
  liveEvents,
  queuedEvents,
  ineffectiveTick,
}: UseCockpitIneffectiveBreakdownParams): IneffectiveBreakdown | null => {
  return useMemo(() => {
    if (!match) return null;
    const homeTeamIds = [match.home_team.id, match.home_team.team_id].filter(
      Boolean,
    ) as string[];
    const awayTeamIds = [match.away_team.id, match.away_team.team_id].filter(
      Boolean,
    ) as string[];

    // PMA-COCK-205 (EPIC-43): NOT deduped, deliberately, and verified, not
    // assumed. computeIneffectiveBreakdown's start/stop pairing (VAR,
    // Timeout, Clock) uses single scalars set-on-start and read-then-
    // cleared-on-stop, not an accumulating counter — reprocessing an exact
    // duplicate re-assigns the SAME scalar value (a true no-op), so a
    // duplicated stoppage event here cannot double a duration total the way
    // it doubles useDisciplinary/useGoalTally/usePlayerStats's counters.
    // See eventOverlapDedup.consumers.test.ts's TimeoutStart/TimeoutStop
    // case (asymmetric duplication, the shape that DID break
    // useCockpitInteractionHandlers) for the verification.
    const combinedEvents = [...liveEvents, ...queuedEvents];

    // Always use local event computation when events are loaded.
    // This ensures optimistic events (e.g., team switches) are reflected
    // immediately rather than waiting for stale server aggregates to refresh.
    if (combinedEvents.length > 0) {
      return computeIneffectiveBreakdown(
        combinedEvents,
        homeTeamIds,
        awayTeamIds,
        Date.now(),
      );
    }

    // Fallback to server aggregates only when no local events are loaded yet
    if (match.ineffective_aggregates) {
      return buildIneffectiveBreakdownFromAggregates(
        match.ineffective_aggregates,
        Date.now(),
      );
    }

    return computeIneffectiveBreakdown(
      combinedEvents,
      homeTeamIds,
      awayTeamIds,
      Date.now(),
    );
  }, [match, liveEvents, queuedEvents, ineffectiveTick]);
};
